import React, { useEffect, useRef, useState } from "react"
import BigCalendar from "../../../../components/compounds/bigCalendar/BigCalendar.compound"
import { DateTime } from "luxon"
import useScreenDimensionsContext from "../../../../hooks/contextConsumers/useScreenDimensionsContext.hook"
import { formatVacationEventsTemplates } from "../../../../utils/bigCalendar.utils"
import Card from "../../../../components/atoms/Card.atom"
import GroupedEventsOverlayPanel from "../../components/GroupedEventsOverlayPanel.compound"
import { BIG_CALENDAR_VIEWS } from "../../../../consts/bigCalendar.consts"
import useFetchAdminVacationEvents from "../../../../hooks/useFetchAdminVacationEvents.hook"
import {
  getVacationState,
  isEventHolidayOfAnyType,
  getManagementVacationsCalendarBounds,
} from "../../../../utils/vacation.utils"
import {
  TEAM_VACATION_REQUESTS_FILTER_TYPES,
  VACATION_STATES,
} from "../../../../consts/modules/vacations.consts"

/** @typedef {import("../../../../services/vacation.service.types").getTeamVacationsRequestsByYearResponse} TeamVacationRequest */

/** @type {import("./CalendarTeamVacationRequests.controller.types")["CalendarTeamVacationRequests"]} */
const CalendarTeamVacationRequests = ({
  refresh,
  selectedPeriod,
  setVisibleModal,
  setSelectedVacationRequest,
  selectedVacationType,
  setSelectedPeriod,
  shouldOnlyShowVacation = null,
  deselectOnlyShowVacation = () => {},
  setPreviousMonthDate = () => {},
  previousMonthDate = null,
  resetSelectedPeriod = () => {},
  setSelectedMonth = () => {},
}) => {
  const [vacationRequestsResponse, setVacationRequestsResponse] = useState([])
  const bounds = getManagementVacationsCalendarBounds()
  const [vacationRequests, setVacationRequests] = useState([])
  const [highlightedVacationRequest, setHighlightedVacationRequest] =
    useState(null)
  const [groupedEvents, setGroupedEvents] = useState([])
  const [currentView, setCurrentView] = useState(BIG_CALENDAR_VIEWS.MONTH)
  const [clickedEvent, setClickedEvent] = useState(null)
  const [loading, setLoading] = useState(false)
  const { screenWidth } = useScreenDimensionsContext()
  const { fetchVacationRequests, fetchHolidays } = useFetchAdminVacationEvents({
    selectedPeriod,
  })
  const [calendarKey, setCalendarKey] = useState(0)
  const opRef = useRef(null)
  const isInternalNavigation = useRef(false)

  useEffect(() => {
    if (shouldOnlyShowVacation) {
      const vacationStartDate = DateTime.fromISO(
        shouldOnlyShowVacation.startDate
      )
      const vacationEndDate = DateTime.fromISO(shouldOnlyShowVacation.endDate)

      const startYear = vacationStartDate.year
      const currYear = currentDate.getFullYear()

      let newCurrDate = vacationStartDate
      if (startYear < currYear) {
        newCurrDate = vacationEndDate
      }

      const onTheSameMonth = currentDate.getMonth() + 1 === newCurrDate.month
      if (!onTheSameMonth) {
        setCurrentDate(newCurrDate.toJSDate())
      }
      setPreviousMonthDate(new Date())

      const vacationRequest = vacationRequests.find(
        (item) => item.id === shouldOnlyShowVacation.id
      )
      if (vacationRequest) {
        setHighlightedVacationRequest(vacationRequest)
      }
    } else {
      if (previousMonthDate) {
        setCurrentDate(previousMonthDate)
        const onDifferentYears =
          currentDate.getFullYear() !== previousMonthDate.getFullYear()
        if (onDifferentYears) {
          resetSelectedPeriod()
        }
      }
      setHighlightedVacationRequest(null)
      setPreviousMonthDate(null)
    }
  }, [shouldOnlyShowVacation])

  const getInitialDate = (/** @type {Array<DateTime>} */ period) => {
    if (!period || period.length === 0) return new Date()

    const now = DateTime.local()
    const startYear = period[0].year

    if (startYear === now.year) {
      return now.toJSDate()
    }
    return period[0].startOf("year").toJSDate()
  }

  const [currentDate, setCurrentDate] = useState(() =>
    getInitialDate(selectedPeriod)
  )

  useEffect(() => {
    const date = DateTime.fromJSDate(currentDate)

    const year = date.year
    const monthNumber = date.month

    setSelectedMonth({
      year,
      month: monthNumber,
    })
  }, [currentDate])

  useEffect(() => {
    fetchData()
    if (isInternalNavigation.current) {
      isInternalNavigation.current = false
    } else {
      const newInitialDate = getInitialDate(selectedPeriod)
      setCurrentDate(newInitialDate)
      setCalendarKey((prev) => prev + 1)
    }
  }, [selectedPeriod, refresh])

  async function filterVacationRequests(data) {
    const filteredVacationRequest = data.filter((item) => {
      if (
        [
          VACATION_STATES.CANCEL_REQUEST,
          VACATION_STATES.APPROVED_AND_PENDING_PLUS_DAYS_PAY_DATE,
          VACATION_STATES.CHANGE_REQUEST,
          VACATION_STATES.PENDING,
        ].includes(getVacationState(item)) &&
        selectedVacationType === TEAM_VACATION_REQUESTS_FILTER_TYPES.PENDING
      ) {
        return true
      }

      if (
        selectedVacationType ===
          TEAM_VACATION_REQUESTS_FILTER_TYPES.REQUESTED &&
        [
          VACATION_STATES.APPROVED_AND_PENDING_PLUS_DAYS_PAY_DATE,
          VACATION_STATES.APPROVED,
        ].includes(getVacationState(item))
      ) {
        return true
      }

      return false
    })
    const holidaysResponse = await fetchHolidays()
    setVacationRequests([...filteredVacationRequest, ...holidaysResponse])
  }

  useEffect(() => {
    filterVacationRequests(vacationRequestsResponse)
  }, [selectedVacationType, refresh])

  const fetchData = async () => {
    setLoading(true)
    setVacationRequests([])
    setVacationRequestsResponse([])
    try {
      const resp = await fetchVacationRequests()
      await filterVacationRequests(resp)
      setVacationRequestsResponse(resp)
    } catch (error) {
      console.error("Error fetching vacation requests:", error)
      setVacationRequests([])
      setVacationRequestsResponse([])
    } finally {
      setLoading(false)
    }
  }

  const handleNavigate = (date, e) => {
    if (highlightedVacationRequest) {
      deselectOnlyShowVacation()
      setPreviousMonthDate(null)
      setHighlightedVacationRequest(null)
    }
    const targetDate = DateTime.fromJSDate(date)
    const [periodStart, periodEnd] = selectedPeriod

    const theoreticalEnd = periodStart
      .plus({ years: 1 })
      .minus({ months: 1 })
      .endOf("month")

    const isAfterCurrentPeriod =
      targetDate.startOf("month") > theoreticalEnd.startOf("month")
    const isBeforeCurrentPeriod =
      targetDate.endOf("month") < periodStart.startOf("month")

    if (isAfterCurrentPeriod || isBeforeCurrentPeriod) {
      isInternalNavigation.current = true

      if (isAfterCurrentPeriod) {
        setSelectedPeriod([
          periodStart.plus({ years: 1 }),
          periodEnd.plus({ years: 1 }),
        ])
      } else {
        setSelectedPeriod([
          periodStart.minus({ years: 1 }),
          periodEnd.minus({ years: 1 }),
        ])
      }
    }
    opRef?.current?.hide()
    setCurrentDate(date)
  }
  const eventPropGetter = (event) => {
    const eventStart = DateTime.fromJSDate(event.start)
    const visibleMonth = DateTime.fromJSDate(currentDate).month
    const isEventOutsideVisibleMonth =
      currentView === BIG_CALENDAR_VIEWS.MONTH &&
      eventStart.month !== visibleMonth
    if (isEventOutsideVisibleMonth) {
      return {
        style: {
          opacity: 0.5,
        },
      }
    }
    if ("groupedEvents" in event) {
      return {
        className: "grouped-event",
      }
    }
    return {}
  }
  const handleEventClick = (
    event,
    e,
    options = {
      groupedEvents: true,
    }
  ) => {
    if (loading) return
    if (isEventHolidayOfAnyType(event)) return

    const selectedEvents =
      currentView === BIG_CALENDAR_VIEWS.MONTH && options.groupedEvents ?
        groupedEvents
      : vacationRequests

    setClickedEvent(event)

    if (!selectedEvents[event.id]?.groupedEvents) {
      setSelectedVacationRequest(event)
      setVisibleModal(true)
      opRef?.current?.hide(e)
      return
    }

    const mouseX = e.clientX
    const mouseY = e.clientY

    const elements = document.elementsFromPoint(mouseX, mouseY)
    const element = elements.find((el) =>
      el.classList.contains("rbc-row-segment")
    )
    if (element) {
      e.target = element
    }

    opRef?.current?.hide(e)
    setTimeout(() => {
      opRef?.current?.show(e)
    }, 0)
  }

  return (
    <Card
      className="calendar-card-container shadow-2"
      style={{ height: screenWidth > 860 ? "90vh" : "72vh", padding: "1rem" }}
    >
      <BigCalendar
        key={calendarKey}
        loading={loading}
        events={vacationRequests}
        showOnlyEvent={highlightedVacationRequest}
        onView={(e) => {
          opRef?.current?.hide(e)
          setCurrentView(e)
        }}
        views={{
          week: screenWidth > 728,
          month: true,
          placeholder: true,
          // year: screenWidth > 728 ? YearView : false,
        }}
        view={screenWidth <= 728 ? BIG_CALENDAR_VIEWS.MONTH : currentView}
        popup
        style={{ height: "100%" }}
        eventPropGetter={eventPropGetter}
        handleNavigate={handleNavigate}
        formatEventsTemplates={formatVacationEventsTemplates}
        handleEventClick={handleEventClick}
        setGroupedEvents={setGroupedEvents}
        tooltipAccessor={(e) => e.prevTitle}
        date={currentDate}
        min={bounds.lastYearPeriod.start}
        max={bounds.nextYearPeriod.end}
      />
      <GroupedEventsOverlayPanel
        opRef={opRef}
        vacationRequests={vacationRequests}
        clickedEvent={clickedEvent}
        handleEventClick={handleEventClick}
      />
    </Card>
  )
}

export default CalendarTeamVacationRequests
